/**
 * Capture bar — the ~3-second capture surface. A textarea with the slash
 * grammar (`/action !dana due:friday send the deck`), a live parse preview
 * chip row, and an @/! autocomplete popover.
 *
 * Enter submits, Shift+Enter adds description lines (the rich `details`
 * body). Typing `/` alone shows the command palette.
 */
import { useMemo, useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/ui";
import { loopRecords, type RecordLinkTarget } from "../../lib/api";
import {
    CMDS, CMD_HELP, detectTrigger, normalizeDate, parseComposer, serializeState, stateToPayload,
    type Cmd, type Directory, type DirectoryEntry, type TriggerHit,
} from "../../lib/grammar";
import { filterDirectory, sigilPool } from "./editor/sigilTriggers";
import { SuggestionMenu, type SuggestionItem, type SuggestionMenuRef } from "./editor/SuggestionMenu";
import CaptureFields, { type SetField } from "./CaptureFields";
import "./day-note.css";

const TYPE_LABEL: Record<Cmd, string> = {
    action: "Action", owe: "Owe", bringup: "Bring up", decision: "Decision",
    note: "Note", remind: "Reminder",
};

export default function CaptureBar({
    directory,
    dayPage,
    originMeetingId,
    parentRecordId,
    placeholder,
    initialText,
    autoFocus,
    inputId,
    extraLinks,
    extraMentions,
    onCreated,
}: {
    directory: Directory;
    /** YYYY-MM-DD the user is viewing — stamped onto every capture. */
    dayPage?: string;
    /** Set when capturing inside a meeting card — anchors the record. */
    originMeetingId?: string;
    /** Set in the replies composer — threads the capture under a record. */
    parentRecordId?: string;
    placeholder?: string;
    /** Seed text (omni-bar handoff: user typed "/…" in the jump field). */
    initialText?: string;
    autoFocus?: boolean;
    /** DOM id for the textarea — lets pages focus it from a shortcut. */
    inputId?: string;
    /** Always-attached `#` links (entity-page capture: everything captured
     *  here is tagged with that entity). Merged with typed # tags. */
    extraLinks?: Array<{ targetType: RecordLinkTarget; targetId: string }>;
    /** Always-@mentioned contacts (contact-page capture: everything lands
     *  on that person's ledger even without typing their name). */
    extraMentions?: string[];
    onCreated?: () => void;
}) {
    const queryClient = useQueryClient();
    const [text, setText] = useState(initialText ?? "");
    const [caret, setCaret] = useState(0);
    // Escape hides the popup until the text changes again.
    const [dismissed, setDismissed] = useState<string | null>(null);
    const [fieldsOpen, setFieldsOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const menuRef = useRef<SuggestionMenuRef>(null);

    const state = useMemo(() => parseComposer(text, directory), [text, directory]);

    // Command palette when the title line is just "/" + optional prefix.
    const firstLine = text.split("\n", 1)[0] ?? "";
    const cmdPrefix = /^\/([a-z]*)$/i.exec(firstLine.trim());
    const cmdSuggestions = cmdPrefix
        ? CMDS.filter((c) => c.startsWith(cmdPrefix[1]!.toLowerCase()))
        : [];

    // @/! autocomplete — only on the title line.
    const trigger: TriggerHit | null = useMemo(() => {
        if (caret > firstLine.length) return null;
        return detectTrigger(text, caret);
    }, [text, caret, firstLine.length]);
    // Same matcher as the notepad's sigil menus — one behaviour everywhere.
    const triggerMatches: DirectoryEntry[] = useMemo(
        () => trigger ? filterDirectory(sigilPool(trigger.char, directory), trigger.query) : [],
        [trigger, directory]);

    const popupItems: SuggestionItem[] = cmdSuggestions.length > 0
        ? cmdSuggestions.map((cmd) => ({ key: cmd, label: `/${cmd}`, hint: CMD_HELP[cmd], kind: "cmd" }))
        : triggerMatches.map((e) => ({ key: e.id, label: e.name, hint: e.kind === "contact" ? null : e.kind, kind: e.kind }));
    const popupOpen = popupItems.length > 0 && dismissed !== text;
    const popupLabel = cmdSuggestions.length > 0 ? "commands"
        : trigger?.char === "@" ? "mention contact" : trigger?.char === "#" ? "tag account / lead / event / meeting" : "assign to";

    const create = useMutation({
        mutationFn: async () => {
            const payload = stateToPayload(state);
            if (!payload) throw new Error(state.cmd === "remind"
                ? "Reminders need a parseable due: date"
                : "Start with a /command and a title");
            if (dayPage) payload.dayPage = dayPage;
            if (originMeetingId) payload.originMeetingId = originMeetingId;
            if (parentRecordId) payload.parentRecordId = parentRecordId;
            if (extraLinks?.length) {
                const merged = [...(payload.links ?? [])];
                for (const l of extraLinks) {
                    if (!merged.some((m) => m.targetType === l.targetType && m.targetId === l.targetId)) merged.push(l);
                }
                payload.links = merged;
            }
            if (extraMentions?.length) {
                const ids = new Set([...(payload.mentionContactIds ?? []), ...extraMentions]);
                if (payload.assigneeContactId) ids.delete(payload.assigneeContactId);
                payload.mentionContactIds = [...ids];
            }
            const res = await loopRecords.create(payload);
            if (!res.ok) throw new Error(res.error);
            return res;
        },
        onSuccess: async () => {
            setText("");
            setFieldsOpen(false);
            await queryClient.invalidateQueries({ queryKey: ["loop"] });
            onCreated?.();
        },
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Capture failed"),
    });

    const applyPick = (index: number) => {
        if (cmdSuggestions.length > 0) {
            const cmd = cmdSuggestions[index]!;
            setText(`/${cmd} ${text.slice(firstLine.length)}`.trimEnd() + " ");
        } else if (trigger) {
            const entry = triggerMatches[index]!;
            const next = text.slice(0, trigger.queryStart) + trigger.char + entry.slug + " " + text.slice(caret);
            setText(next);
        }
        textareaRef.current?.focus();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (popupOpen) {
            if (e.key === "Escape") { e.preventDefault(); setDismissed(text); return; }
            if (menuRef.current?.onKeyDown(e.nativeEvent)) { e.preventDefault(); return; }
        }
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!create.isPending) create.mutate();
        }
    };

    // Structured fields ↔ text: edits re-serialize the whole line.
    const setField: SetField = (key, value) => setText(serializeState({ ...state, [key]: value }, directory));

    const syncCaret = (el: HTMLTextAreaElement) => setCaret(el.selectionStart ?? 0);
    const dueIso = state.due ? normalizeDate(state.due) : null;
    const assigneeName = state.assignee
        ? [...directory.contacts, ...directory.companies].find((e) => e.id === state.assignee)?.name ?? null
        : null;

    return (
        <div className="loop-capture">
            <textarea
                ref={textareaRef}
                id={inputId}
                autoFocus={autoFocus}
                className="loop-capture-input"
                placeholder={placeholder ?? "/action !dana due:friday send the deck"}
                rows={text.includes("\n") ? 3 : 1}
                value={text}
                onChange={(e) => { setText(e.target.value); syncCaret(e.target); }}
                onKeyDown={onKeyDown}
                onKeyUp={(e) => syncCaret(e.currentTarget)}
                onClick={(e) => syncCaret(e.currentTarget)}
            />
            {popupOpen && (
                <div className="loop-capture-popup">
                    <SuggestionMenu ref={menuRef} label={popupLabel} items={popupItems} onSelect={applyPick} />
                </div>
            )}
            {state.cmd && !fieldsOpen && (
                <div className="loop-capture-preview">
                    <span className={`loop-type-badge loop-type--${stateToPayload(state)?.type ?? state.cmd}`}>
                        {TYPE_LABEL[state.cmd]}
                    </span>
                    {assigneeName && <span className="loop-chip">→ {assigneeName}</span>}
                    {state.due && (
                        <span className={`loop-chip ${dueIso ? "" : "loop-chip--error"}`}>
                            due {dueIso ?? `“${state.due}”?`}
                        </span>
                    )}
                    {state.links.map((l) => (
                        <span key={l.id} className="loop-chip">
                            #{directory.targets.find((t) => t.id === l.id)?.name ?? l.id}
                        </span>
                    ))}
                    {state.mentions.length > 0 && <span className="loop-chip">@{state.mentions.length}</span>}
                    {!state.content.trim() && <span className="muted" style={{ fontSize: 12 }}>… add a title</span>}
                    <span className="spacer" />
                    <button type="button" className="loop-fields-chip" title="Open structured fields"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setFieldsOpen(true)}>
                        <SlidersHorizontal size={11} /> fields
                    </button>
                </div>
            )}
            {state.cmd && fieldsOpen && (
                <CaptureFields state={state} directory={directory} setField={setField}
                    onCancel={() => { setFieldsOpen(false); textareaRef.current?.focus(); }}
                    onSubmit={() => { if (!create.isPending) create.mutate(); }}
                    canSubmit={!!stateToPayload(state) && !create.isPending} />
            )}
        </div>
    );
}
